/**
 * Barra da lista do layout A + C: fica logo acima de toda tabela/lista e presa no topo ao rolar (o cabeçalho da
 * `DataTable` prende logo abaixo dela).
 *
 * `<ListToolbar label search chips filters view export actions onClear>{tabela}</ListToolbar>`
 * - `search` (obrigatória): `{ value, onChange, label?, placeholder? }` — campo de busca da lista;
 * - `chips`: `{ items, value, onChange, label }` (as props de `QuickChips`) ou um nó pronto;
 * - `filters`: `{ groups, value, onChange }` — as mesmas facetas que a tela põe no painel de contexto (`FacetFilters`).
 *   A barra mostra os filtros ativos como etiquetas removíveis ("Modo: Ativo ×") e "Limpar"; o botão "Filtros (n)"
 *   só aparece quando o painel não está à vista (recolhido ou janela estreita) e abre o painel como gaveta;
 * - `view`: o `ViewToggle` (quando a lista tem outra visão); `export`: o `ExportMenu`; `actions`: botões a mais;
 * - `onClear`: o que "Limpar" faz (padrão: tira as facetas e a busca);
 * - `children`: a tabela; a barra mede a própria altura e o cabeçalho da tabela prende logo abaixo dela.
 */
import { useI18n, type CommonKey } from '@hive/i18n';
import { isValidElement, useLayoutEffect, useRef, type ReactNode } from 'react';
import { facetCount, facetWithout, QuickChips, type FacetGroup, type FacetValue, type QuickChip } from './filters';
import { Icon } from './icons';
import { cx } from './layout';
import { useContextPanelState } from './shellSlots';

export interface ListToolbarProps {
  /** Nome acessível da barra (padrão: "Ferramentas da lista"). */
  label?: string;
  search: { value: string; onChange: (v: string) => void; label?: string; placeholder?: string };
  chips?: { items: QuickChip[]; value?: string | string[] | null; onChange: (id: string) => void; label: string } | ReactNode;
  filters?: { groups: FacetGroup[]; value: FacetValue; onChange: (next: FacetValue, groupId: string) => void };
  view?: ReactNode;
  export?: ReactNode;
  actions?: ReactNode;
  onClear?: () => void;
  children?: ReactNode;
  className?: string;
}

const text = (n: ReactNode, fallback: string) => (typeof n === 'string' || typeof n === 'number' ? String(n) : fallback);

export function ListToolbar({ label, search, chips, filters, view, export: exportNode, actions, onClear, children, className }: ListToolbarProps) {
  const { t, fmt } = useI18n<CommonKey>();
  const panel = useContextPanelState();
  const bar = useRef<HTMLDivElement>(null);
  const block = useRef<HTMLDivElement>(null);
  // o cabeçalho da tabela prende logo abaixo da barra
  useLayoutEffect(() => {
    const el = bar.current;
    const host = block.current;
    if (!el || !host || typeof ResizeObserver === 'undefined') return;
    const set = () => host.style.setProperty('--ltb-h', `${el.offsetHeight}px`);
    const ro = new ResizeObserver(set);
    ro.observe(el);
    set();
    return () => ro.disconnect();
  }, []);
  const n = facetCount(filters?.value);
  const tags = filters
    ? filters.groups.flatMap((g) =>
        (filters.value[g.id] ?? []).map((v) => {
          const opt = g.options.find((o) => o.value === v);
          return { g, v, name: `${text(g.title, g.id)}: ${text(opt?.label ?? v, v)}` };
        }),
      )
    : [];
  const clear = () => {
    if (onClear) return onClear();
    filters?.onChange({}, '');
    search.onChange('');
  };
  const chipsNode =
    chips && typeof chips === 'object' && !isValidElement(chips) && 'items' in (chips as object) ? (
      <QuickChips {...(chips as { items: QuickChip[]; value?: string | string[] | null; onChange: (id: string) => void; label: string })} />
    ) : (
      (chips as ReactNode)
    );
  return (
    <div className={cx('list-block', className)} ref={block}>
      <div className="ltb" role="toolbar" aria-label={label ?? t('ui.ltb.label')} ref={bar}>
        <div className="ltb-row">
          <label className="ltb-search">
            <Icon name="search" size="sm" />
            <input type="search" aria-label={search.label ?? t('ui.ltb.search')} placeholder={search.placeholder ?? search.label ?? t('ui.ltb.search')} value={search.value} onChange={(e) => search.onChange(e.target.value)} />
          </label>
          {chipsNode && <div className="ltb-chips">{chipsNode}</div>}
          <span className="ltb-gap" />
          {filters && panel && !panel.visible && (
            <button type="button" className={cx('btn btn-sm ltb-filters', n > 0 && 'on')} aria-haspopup="dialog" onClick={panel.open}>
              <Icon name="rule" size="sm" />
              {n > 0 ? t('ui.ltb.filtersN', { n: fmt.number(n) }) : t('ui.ltb.filters')}
            </button>
          )}
          {view}
          {actions}
          {exportNode}
        </div>
        {tags.length > 0 && (
          <ul className="ltb-tags" aria-label={t('ui.ltb.active')}>
            {tags.map((x) => (
              <li key={`${x.g.id}:${x.v}`}>
                <button type="button" className="ltb-tag" aria-label={t('ui.ltb.remove', { name: x.name })} title={t('ui.ltb.remove', { name: x.name })} onClick={() => filters?.onChange(facetWithout(filters.value, x.g.id, x.v), x.g.id)}>
                  {x.name}
                  <Icon name="x" size="sm" />
                </button>
              </li>
            ))}
            <li>
              <button type="button" className="ltb-clear" onClick={clear}>
                {t('ui.ltb.clear')}
              </button>
            </li>
          </ul>
        )}
      </div>
      {children}
    </div>
  );
}
